import { reduxForm } from 'redux-form/immutable';
import {
  compose,
  mapProps,
  withProps,
  withState,
  shouldUpdate,
  branch,
} from 'recompose';
import { List, Map } from 'immutable';
import moment from 'moment';
import range from 'lodash/range';

import withPagination from '../../../../components/PaginatedItems/withPagination';
import withAvailableDates from '../../../../containers/withAvailableDates';
import { withPatch } from '../../../../entities/Entity/containers';
import debounceProp from '../../../../lib/debounceProp';
import withNotifications from '../../../../components/Notifications/withNotifications';
import withCurrentStaffingCompanyGroup from '../../../../containers/withCurrentStaffingCompanyGroup';
import withAvailableDateSetting from '../../../../containers/withAvailableDateSetting';
import requireProps from '../../../../lib/requireProps';
import withStaffingCompanyGroup from '../../../../../public/containers/withStaffingCompanyGroup';
import * as styles from './styles.css';
import { withTranslation } from 'react-i18next';

const PREFETCH_MONTHS = 3;

const periodStatusFieldsProps = (setting) => (index) => {
  if (setting[`period${index}Enabled`]) {
    return {
      [`period${index}status`]: 'available',
    };
  }
  return null;
};

const getPeriodsHighlightsForDate = (date, allPeriodHighlights, hasDayAfter, hasDayBefore) => {
  if (!allPeriodHighlights) {
    return undefined;
  }
  const todayPeriods = allPeriodHighlights.get(date.format('YYYY-MM-DD'));

  if (hasDayAfter) {
    const key = moment(date).add(-1, 'day').format('YYYY-MM-DD');
    const prevDate = allPeriodHighlights.get(key);
    const periods = todayPeriods || new List([0, 0, 0]);
    return periods.unshift(prevDate ? prevDate.last() : 0);
  }
  if (hasDayBefore) {
    const key = moment(date).add(1, 'day').format('YYYY-MM-DD');
    const nextDate = allPeriodHighlights.get(key);
    const periods = todayPeriods || new List([0, 0, 0]);
    return periods.shift(nextDate ? nextDate.last() : 0);
  }
  return todayPeriods;
};

const availableDateSettingHasDayAfter = (availableDateSetting) => {
  for (let i = 1; i <= 8; i += 1) {
    if (availableDateSetting[`period${i}DayAfter`]) {
      return true;
    }
  }
  return false;
};

const availableDateSettingHasDayBefore = (availableDateSetting) => {
  for (let i = 1; i <= 8; i += 1) {
    if (availableDateSetting[`period${i}DayBefore`]) {
      return true;
    }
  }
  return false;
};

const availableDateSettingEnabledAmount = (availableDateSetting) => {
  let amount = 0;
  for (let i = 1; i <= 8; i += 1) {
    if (availableDateSetting[`period${i}Enabled`]) {
      amount += 1;
    }
  }
  return amount;
};

export default (listUrl, patchUrl, form) => compose(
  withTranslation(),
  shouldUpdate((current, next) => {
    if (current.id !== next.id) {
      return true;
    }
    return false;
  }),
  withState('month', 'onChangeMonth', moment().format('YYYY-MM-DD')),
  withState('lastValues', 'updateLastValues', new List()),
  withProps(({ id, month }) => ({
    url: listUrl({
      resourceId: id,
    }),
    fetchUrl: listUrl({
      resourceId: id,
      from: moment(month).subtract(PREFETCH_MONTHS, 'month').startOf('month').format('YYYY-MM-DD'),
      to: moment(month).add(PREFETCH_MONTHS, 'month').endOf('month').format('YYYY-MM-DD'),
      limit: (PREFETCH_MONTHS + 1) * 31,
    }),
  })),
  debounceProp('fetchUrl'),
  withPagination(),
  mapProps(({ ids, workPreference, ...props }) => ({
    availableDateIds: ids,
    areAvailabilitiesLoading: props.isLoading,
    workPreference,
    assumedAvailableWeekdays: workPreference && workPreference.assumedAvailableWeekdays ? true : false,
    ...props,
  })),
  branch(
    ({ isPublic }) => !isPublic,
    withCurrentStaffingCompanyGroup,
    compose(
      withStaffingCompanyGroup,
      mapProps(({ staffingCompanyGroup, ...props }) => ({
        ...props,
        currentStaffingCompanyGroup: staffingCompanyGroup,
      })),
    ),
  ),
  mapProps(({ availableDates, currentStaffingCompanyGroup, ...props }) => ({
    availableDateSettingId: currentStaffingCompanyGroup
      ? currentStaffingCompanyGroup.availableDateSetting : undefined,
    ...props,
  })),
  withAvailableDateSetting,
  withAvailableDates,
  requireProps(['availableDateSetting']),
  withProps(({ availableDates, availableDateSetting, assumedAvailableWeekdays, month }) => {
    const getStateColor = (status, isAssumed = false) => {
      if (isAssumed && status === 'available') {
        return 'rgb(214, 234, 248)'; // Light blue for assumed available
      }
      switch (status) {
        case 'available':
          return 'rgb(223, 240, 216)';
        case 'unknown':
          return undefined;
        default:
          return 'rgb(249, 189, 187)';
      }
    };

    const isWeekday = (date) => {
      const day = moment(date).day();
      return day !== 0 && day !== 6; // Not Sunday (0) or Saturday (6)
    };

    const periodStatusField = periodStatusFieldsProps(availableDateSetting);
    const indexes = range(1, 9);
    const periodStatusKeys = indexes
      .map((i) => periodStatusField(i))
      .filter((o) => o)
      .reduce((acc, curr) => {
        const key = Object.keys(curr)[0];
        return acc.push(key);
      }, new List());

    // Build periodHighlights from explicit entries
    let periodHighlights = availableDates
      .sort((a, b) => moment(a.date).diff(b.date))
      .reduce((acc, availableDate) => (
        acc.set(
          availableDate.date,
          periodStatusKeys.map((key) => getStateColor(availableDate[key], false)),
        )), new Map());

    // Add assumed availability for workers with assumed weekday availability
    if (assumedAvailableWeekdays) {
      const startDate = moment(month).subtract(PREFETCH_MONTHS, 'month').startOf('month');
      const endDate = moment(month).add(PREFETCH_MONTHS, 'month').endOf('month');
      const currentDate = startDate.clone();

      while (currentDate.isSameOrBefore(endDate)) {
        const dateKey = currentDate.format('YYYY-MM-DD');
        if (!periodHighlights.has(dateKey) && isWeekday(currentDate)) {
          // Add assumed available (blue) for all enabled periods
          periodHighlights = periodHighlights.set(
            dateKey,
            periodStatusKeys.map(() => getStateColor('available', true)),
          );
        }
        currentDate.add(1, 'day');
      }
    }

    return {
      periodHighlights,
    };
  }),
  withProps(({ periodHighlights, availableDateSetting }) => {
    const hasDayAfter = availableDateSettingHasDayAfter(availableDateSetting);
    const hasDayBefore = availableDateSettingHasDayBefore(availableDateSetting);

    const getPeriodIndex = (index, maxIndex) => {
      if (hasDayAfter) {
        if (index === 0) {
          return maxIndex;
        }
      }
      if (hasDayBefore) {
        if (index === maxIndex) {
          return 0;
        }
      }
      return index;
    };

    const newPeriodHighlights = periodHighlights.map((arr, date) => {
      const dateMoment = moment(date);
      const periodHighlightsForDate = getPeriodsHighlightsForDate(
        dateMoment,
        periodHighlights,
        hasDayAfter,
        hasDayBefore,
      );
      if (!periodHighlightsForDate) {
        return {
          periodHighlights: [],
        };
      }
      const maxIndex = availableDateSettingEnabledAmount(availableDateSetting);
      return periodHighlightsForDate.map((background, index) => {
        const availableSettingIndex = getPeriodIndex(index, maxIndex);
        return {
          name: availableDateSetting[`period${availableSettingIndex}Name`],
          background,
        };
      });
    });

    return {
      periodHighlights: newPeriodHighlights,
      hasSplittedDay: hasDayAfter || hasDayBefore,
    };
  }),
  withProps(({ availableDateSetting }) => {
    if (!availableDateSetting) {
      return {};
    }
    const periodStatusField = periodStatusFieldsProps(availableDateSetting);
    const indexes = range(1, 9);
    const periodStatusFields = indexes
      .map((i) => periodStatusField(i))
      .filter((o) => o)
      .reduce((acc, curr) => {
        const key = Object.keys(curr)[0];
        return {
          ...acc,
          [key]: curr[key],
        };
      }, {});
    return {
      initialValues: {
        // it shouldn't have any initialValues since selected dates = adding new dates
        availableDates: new List(),
        ...periodStatusFields,
      },
    };
  }),
  withNotifications,
  withPatch({
    props: (props) => ({
      ...props,
      isLoading: props.isLoading || props.areAvailabilitiesLoading,
      onSubmit: (values, dispatch, { reset }) => {
        const { availableDates, ...periodStatuses } = values.toJS();
        props.patch(
          {
            url: patchUrl({ resourceId: props.id }),
            entity: availableDates.map((date) => ({
              date,
              ...periodStatuses,
            })),
          },
        ).then(() => {
          props.refresh();
          reset();
          props.addSuccess({ message: props.t('Tillgängligheten har uppdaterats!') });
          if (props.triggerRefresh) {
            props.triggerRefresh();
          }
        });
      },
    }),
  }),
  reduxForm(
    {
      form,
      enableReinitialize: true,
      keepDirtyOnReinitialize: true,
    },
  ),
);
